"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { fetchPlans, fetchSubscriptionHistory, fetchEntitlement, fetchDiscounts, removeDiscount } from "../../../services/billingService";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { formatPaise, formatBillingDate, getSubscriptionGraceInfo } from "../../../utils/billingFormat";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "react-hot-toast";
import { History, Receipt, FileText, ClipboardList, ChevronRight, IndianRupee, Users, Tag, X, Gift, Lock } from "lucide-react";
import RecordPaymentModal from "./RecordPaymentModal";
import ChangeUserLimitModal from "./ChangeUserLimitModal";
import EditTrialModal from "./EditTrialModal";
import ApplyDiscountModal from "./ApplyDiscountModal";
import StartTrialModal from "./StartTrialModal";

const SchoolBillingDetail = () => {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [togglingBanner, setTogglingBanner] = useState(false);
  const [togglingBillingVisible, setTogglingBillingVisible] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [updatingNudge, setUpdatingNudge] = useState(false);
  const [updatingGracePeriod, setUpdatingGracePeriod] = useState(false);
  const [updatingPlans, setUpdatingPlans] = useState(false);
  const [removingDiscountId, setRemovingDiscountId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [plansRes, schoolsRes, historyRes, usageRes, discountsRes] = await Promise.all([
        fetchPlans({ activeOnly: true }),
        axiosInstance.get(API_ENDPOINTS.schoolsStats),
        fetchSubscriptionHistory(schoolId),
        fetchEntitlement(schoolId),
        fetchDiscounts(schoolId),
      ]);
      const schools = schoolsRes.data?.data?.schools || [];
      const found = schools.find((s) => String(s._id) === String(schoolId));
      setSchool(found || null);
      setAllPlans(plansRes.data || []);
      setSubscription((historyRes.data || [])[0] || null);
      setUsage(usageRes.data || null);
      setDiscounts(discountsRes.data || []);
    } catch (err) {
      setError(extractApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  const handleRemoveDiscount = async (discount) => {
    setRemovingDiscountId(discount._id);
    try {
      await removeDiscount(schoolId, discount.plan?._id || discount.plan);
      toast.success("Discount removed");
      await load();
    } catch (err) {
      toast.error(extractApiErrorMessage(err));
    } finally {
      setRemovingDiscountId(null);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleExpiryBanner = async (checked) => {
    setTogglingBanner(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        billingExpiryBannerEnabled: checked,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
      }
    } catch (err) {
      window.alert(extractApiErrorMessage(err) || "Failed to update expiry banner setting");
    } finally {
      setTogglingBanner(false);
    }
  };

  const handleToggleActive = async (checked) => {
    setTogglingActive(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        isActive: checked,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success(checked ? "Customer reactivated" : "Customer deactivated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update active status");
    } finally {
      setTogglingActive(false);
    }
  };

  const handleToggleBillingVisible = async (checked) => {
    setTogglingBillingVisible(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        billingVisible: checked,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success("Billing visibility updated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update billing visibility");
    } finally {
      setTogglingBillingVisible(false);
    }
  };

  const handleAllPlansToggle = async (checked) => {
    setUpdatingPlans(true);
    try {
      const newPlanIds = checked ? null : [];
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        visiblePlanIds: newPlanIds,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success("Plan visibility updated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update plan visibility");
    } finally {
      setUpdatingPlans(false);
    }
  };

  const handlePlanCheckboxChange = async (planId, isChecked) => {
    setUpdatingPlans(true);
    try {
      let currentVisible = Array.isArray(school?.visiblePlanIds)
        ? [...school.visiblePlanIds]
        : allPlans.map((p) => p._id);

      if (isChecked) {
        if (!currentVisible.some((id) => String(id) === String(planId))) {
          currentVisible.push(planId);
        }
      } else {
        currentVisible = currentVisible.filter((id) => String(id) !== String(planId));
      }
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        visiblePlanIds: currentVisible,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success("Plan visibility updated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update plan visibility");
    } finally {
      setUpdatingPlans(false);
    }
  };

  const handleNudgeFrequencyChange = async (value) => {
    setUpdatingNudge(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        nudgeFrequency: value,
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success("Nudge frequency updated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update nudge frequency");
    } finally {
      setUpdatingNudge(false);
    }
  };

  const handleGracePeriodChange = async (value) => {
    setUpdatingGracePeriod(true);
    try {
      const res = await axiosInstance.patch(API_ENDPOINTS.school(schoolId), {
        gracePeriodDays: Number(value),
      });
      if (res.data?.status === "success" && res.data.data?.school) {
        setSchool(res.data.data.school);
        toast.success("Grace period updated");
      }
    } catch (err) {
      toast.error(extractApiErrorMessage(err) || "Failed to update grace period");
    } finally {
      setUpdatingGracePeriod(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading billing details...</div>;
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}`)}>
          Back to Customer
        </Button>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>
      </div>
    );
  }

  if (school && school.tenantType !== "industry") {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}`)}>
          Back to Customer
        </Button>
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
          Billing is not available for this customer — it is not an Industry Inventory tenant.
        </div>
      </div>
    );
  }

  const isActiveSub = subscription?.status === "ACTIVE";
  const isActiveTrial = isActiveSub && subscription?.type === "TRIAL";
  const plan = subscription?.plan;
  const isDeactivateFrozen = school?.isActive !== false && isActiveSub;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing — {school?.name}</h1>
          <p className="text-sm text-gray-500">{school?.domain}</p>
        </div>
        <Button variant="outline" onClick={() => navigate(`/dashboard/schools/${schoolId}`)}>
          Back to Customer
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-1.5">
            Customer Active
            {isDeactivateFrozen && <Lock className="h-3.5 w-3.5 text-gray-400" />}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {isDeactivateFrozen
              ? `This customer has an active ${subscription.type === "TRIAL" ? "trial" : "subscription"} (until ${formatBillingDate(subscription.endDate)}) — can't be deactivated until it ends.`
              : "Off lets schooladmin and staff still log in, but they only see a Renew / Contact Us screen — no other page loads for them."}
          </p>
        </div>
        <Switch
          checked={school?.isActive !== false}
          disabled={togglingActive || isDeactivateFrozen}
          onCheckedChange={handleToggleActive}
        />
      </div>

      {!subscription && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800 text-sm">
          No plan/subscription yet for this customer.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500 font-medium">Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-bold text-gray-900">
              {subscription?.type === "TRIAL" ? "Trial Access" : (plan?.name || "-")}
            </p>
            {subscription && subscription.type !== "TRIAL" && (
              <p className="text-xs text-gray-500 mt-1">{formatPaise(subscription.totalAmount)} / {subscription.durationMonths}mo</p>
            )}
            {subscription && subscription.type === "TRIAL" && (
              <p className="text-xs text-blue-600 mt-1 font-medium">Free Evaluation Trial</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500 font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            {subscription ? (() => {
              const graceInfo = getSubscriptionGraceInfo(subscription);
              return (
                <Badge className={graceInfo.badgeClass} variant={graceInfo.variant}>
                  {graceInfo.label}
                </Badge>
              );
            })() : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500 font-medium">Users</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-lg font-bold text-gray-900">
              {usage?.currentUsers ?? "-"} / {usage?.maxUsers ?? "-"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-500 font-medium">Dates</CardTitle>
          </CardHeader>
          <CardContent>
            {subscription ? (
              <p className="text-sm text-gray-900">{formatBillingDate(subscription.startDate)} → {formatBillingDate(subscription.endDate)}</p>
            ) : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </CardContent>
        </Card>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Customer Controls</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Billing Section Visible</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                If off, the tenant&apos;s Billing page shows a friendly &quot;not available&quot; message.
              </p>
            </div>
            <Switch
              checked={school?.billingVisible !== false}
              disabled={togglingBillingVisible}
              onCheckedChange={handleToggleBillingVisible}
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Show Expiry Banner</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                When on, this customer sees a &quot;subscription expired&quot; banner on their own billing screen once expired.
              </p>
            </div>
            <Switch
              checked={school?.billingExpiryBannerEnabled !== false}
              disabled={togglingBanner}
              onCheckedChange={handleToggleExpiryBanner}
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Expiry Nudge Frequency</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                How often the &quot;your plan expired&quot; reminder pops up when the tenant opens the app.
              </p>
            </div>
            <select
              value={school?.nudgeFrequency || "daily"}
              disabled={updatingNudge}
              onChange={(e) => handleNudgeFrequencyChange(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none shrink-0"
            >
              <option value="off">Off (never pop up)</option>
              <option value="every_login">Every app open</option>
              <option value="daily">Daily (at most once/day)</option>
              <option value="every_3_days">Every 3 days</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Grace Period</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                How many days after expiry this customer keeps working normally before staff get locked out.
              </p>
            </div>
            <select
              value={school?.gracePeriodDays ?? 15}
              disabled={updatingGracePeriod}
              onChange={(e) => handleGracePeriodChange(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm focus:border-blue-500 focus:outline-none shrink-0"
            >
              <option value="7">7 days</option>
              <option value="15">15 days</option>
            </select>
          </div>

          {/* Plans visible to this customer (multi-select) */}
          <div className="md:col-span-2 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Plans Visible to This Customer</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Restrict which plans show up on the tenant&apos;s &quot;Pay Now&quot; screen.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={school?.visiblePlanIds === null || school?.visiblePlanIds === undefined}
                  disabled={updatingPlans}
                  onChange={(e) => handleAllPlansToggle(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                />
                All Plans
              </label>
            </div>

            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 pt-1 ${school?.visiblePlanIds == null ? "opacity-40 pointer-events-none" : ""}`}>
              {allPlans.map((p) => {
                const isChecked = school?.visiblePlanIds
                  ? school.visiblePlanIds.some((id) => String(id) === String(p._id))
                  : true;
                return (
                  <label
                    key={p._id}
                    className="flex items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50/50 px-3.5 py-2.5 text-xs font-medium text-gray-800 transition-colors cursor-pointer hover:bg-gray-100"
                  >
                    <input
                      type="checkbox"
                      value={p._id}
                      checked={isChecked}
                      disabled={updatingPlans}
                      onChange={(e) => handlePlanCheckboxChange(p._id, e.target.checked)}
                      className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span>
                      {p.name} ({p.durationMonths}mo)
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Button size="lg" className="gap-2" onClick={() => setModal("payment")}>
            <IndianRupee className="h-4 w-4" /> Record Payment
          </Button>
          <Button size="lg" className="gap-2" variant="secondary" disabled={isActiveSub} onClick={() => setModal("trial")}>
            <Gift className="h-4 w-4" /> Start Trial
          </Button>
          <Button size="lg" className="gap-2" variant="secondary" disabled={!isActiveSub} onClick={() => setModal("userLimit")}>
            <Users className="h-4 w-4" /> Change User Limit
          </Button>
          <Button size="lg" className="gap-2" variant="secondary" disabled={!isActiveTrial} onClick={() => setModal("editTrial")}>
            <Gift className="h-4 w-4" /> Edit Trial End Date
          </Button>
          <Button size="lg" className="gap-2" variant="secondary" onClick={() => setModal("discount")}>
            <Tag className="h-4 w-4" /> Apply Discount
          </Button>
        </div>
      </div>

      {discounts.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Active Discounts</h2>
          <div className="space-y-2">
            {discounts.map((discount) => (
              <div
                key={discount._id}
                className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-500/10">
                    <Tag className="h-4 w-4 text-purple-600" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{discount.plan?.name || "Plan"}</p>
                    <p className="text-xs text-gray-500">{formatPaise(discount.discountAmount)} off</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={removingDiscountId === discount._id}
                  onClick={() => handleRemoveDiscount(discount)}
                >
                  <X className="h-4 w-4 mr-1" /> Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <LinkOutCard
          icon={History}
          color="indigo"
          title="Subscription History"
          description="Every plan/subscription this customer has had."
          onClick={() => navigate(`/dashboard/schools/${schoolId}/billing/subscriptions`)}
        />
        <LinkOutCard
          icon={Receipt}
          color="blue"
          title="Payments"
          description="Every payment recorded, manual or Razorpay."
          onClick={() => navigate(`/dashboard/schools/${schoolId}/billing/payments`)}
        />
        <LinkOutCard
          icon={FileText}
          color="green"
          title="Invoices"
          description="Invoices issued for this customer's payments."
          onClick={() => navigate(`/dashboard/schools/${schoolId}/billing/invoices`)}
        />
        <LinkOutCard
          icon={ClipboardList}
          color="amber"
          title="Audit Log"
          description="Every billing action taken by a Super Admin."
          onClick={() => navigate(`/dashboard/schools/${schoolId}/billing/audit`)}
        />
      </div>

      <RecordPaymentModal
        isOpen={modal === "payment"}
        onClose={() => setModal(null)}
        onSaved={load}
        schoolId={schoolId}
        hasActiveSubscription={isActiveSub}
      />
      <StartTrialModal
        isOpen={modal === "trial"}
        onClose={() => setModal(null)}
        onSaved={load}
        schoolId={schoolId}
        schoolName={school?.name}
      />
      <ChangeUserLimitModal
        isOpen={modal === "userLimit"}
        onClose={() => setModal(null)}
        onSaved={load}
        schoolId={schoolId}
        currentMaxUsers={subscription?.maxUsers}
      />
      <EditTrialModal
        isOpen={modal === "editTrial"}
        onClose={() => setModal(null)}
        onSaved={load}
        schoolId={schoolId}
        currentEndDate={subscription?.endDate}
      />
      <ApplyDiscountModal
        isOpen={modal === "discount"}
        onClose={() => setModal(null)}
        onSaved={load}
        schoolId={schoolId}
      />
    </div>
  );
};

const COLOR_CLASSES = {
  indigo: { bg: "bg-indigo-500/10", text: "text-indigo-600" },
  blue: { bg: "bg-blue-500/10", text: "text-blue-600" },
  green: { bg: "bg-green-500/10", text: "text-green-600" },
  amber: { bg: "bg-amber-500/10", text: "text-amber-600" },
};

function LinkOutCard({ icon: Icon, color, title, description, onClick }) {
  const colorClasses = COLOR_CLASSES[color] || COLOR_CLASSES.blue;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm text-left transition-colors hover:border-gray-200 hover:bg-gray-50"
    >
      <div className="flex items-center gap-2.5">
        <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${colorClasses.bg}`}>
          <Icon className={`h-4 w-4 ${colorClasses.text}`} />
        </span>
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
    </button>
  );
}

export default SchoolBillingDetail;
